/**
 * Gradient ramps with no visible edge.
 *
 * A linear ramp holds a value flat and then, at one position, begins changing at a constant
 * rate. The value is continuous there but its RATE of change is not, and the eye reads that
 * corner as a line — a Mach band — sitting exactly where the fade begins. Lengthening the ramp
 * spreads the band out but never removes it, because the corner is in the derivative rather
 * than in the distance.
 *
 * Smoothstep has zero slope at both ends, so a ramp built from it leaves the flat region
 * tangentially and arrives at the next one tangentially. There is no corner at either end for
 * the eye to find.
 *
 * Shared because every fade in the canvas has the same problem: the reading column's top and
 * bottom, the sources list, the thinking stream, and the ground behind the answer. They were
 * each written as two or three hand-authored stops, and each showed the same edge.
 */

/**
 * How many stops approximate the curve.
 *
 * Browsers interpolate LINEARLY between stops, so the curve is sampled rather than drawn. Each
 * remaining corner is a small fraction of the single one it replaces, and they are spread out
 * instead of concentrated. Raising this costs nothing but a longer string.
 */
export const FADE_SAMPLES = 16

const smoothstep = (t: number) => t * t * (3 - 2 * t)

/**
 * A smoothstep ramp, as gradient colour stops ready to drop into a `linear-gradient(...)`.
 *
 * The caller supplies both mappings because positions vary — px, percentages, `calc()` from the
 * far edge — and so do values: a mask ramps black's alpha, while a background ramps a colour
 * mix. Neither can be interpolated generically, and both are trivial for the caller to express.
 *
 * @param position where sample `t` (0 → 1) sits, as any CSS position
 * @param value    the colour at eased progress `e` (0 → 1)
 */
export function easedRamp(
  position: (t: number) => string,
  value: (eased: number) => string,
  samples: number = FADE_SAMPLES,
): string {
  return Array.from({ length: samples + 1 }, (_, index) => {
    const t = index / samples
    return `${value(smoothstep(t))} ${position(t)}`
  }).join(", ")
}

/** Black at a given alpha — the only colour a mask needs, since masks read the alpha channel. */
export function maskAlpha(alpha: number): string {
  return `rgb(0 0 0 / ${alpha.toFixed(4)})`
}

/* ── the app's chrome, and the fade beneath it ────────────────────────────────────────────── */

/**
 * The height of the app header and of the app footer. They are the same bar at opposite ends
 * of the viewport and are never a different size from each other.
 */
export const CHROME_HEIGHT_PX = 64

/** How far past the chrome the content takes to arrive at full strength. */
export const CHROME_RAMP_PX = 78

/** The first pixel past the chrome at which content is drawn undimmed. */
export const CHROME_FADE_END_PX = CHROME_HEIGHT_PX + CHROME_RAMP_PX

/**
 * A downward mask that holds the chrome's own height completely clear and then eases content
 * in — so there is always a full bar's depth of nothing but background behind the header and
 * the footer, and no edge where the fade begins.
 *
 * Two DIMENSIONLESS custom properties switch it, 1 for on and 0 for off:
 *
 *   --chrome-fade   whether the shell fades at all. Off for a page that draws its own full
 *                   viewport and masks its own content (the research canvas), where a second
 *                   fade over the top of the first would dim the same pixels twice.
 *   --chrome-foot   whether there is a footer. Off until a page fills the footer slot.
 *
 * They multiply rather than override, so no two rules ever set the same property and the
 * result never depends on which stylesheet rule happens to win. A canvas page that fills the
 * footer gets a footer and no fade, which neither flag could express alone.
 *
 * Flags rather than lengths because the values have to be settable from CSS — whether a page
 * HAS a footer is something only CSS knows, since the footer is a portal target that a page
 * fills or doesn't and the shell reads its emptiness with `:has()`. A length would have to be
 * written out again at the switch, in a second place, in a unit this file no longer controls.
 * Multiplying by nought collapses that end of the mask to nothing instead, and every
 * measurement stays here.
 */
const head = (px: number) =>
  `calc(${px.toFixed(2)}px * var(--chrome-fade, 1))`
const foot = (px: number) =>
  `calc(100% - ${px.toFixed(2)}px * var(--chrome-foot, 0) * var(--chrome-fade, 1))`

export const CHROME_MASK = `linear-gradient(to bottom,
  ${maskAlpha(0)} 0px,
  ${easedRamp(
    (t) => head(CHROME_HEIGHT_PX + t * CHROME_RAMP_PX),
    (eased) => maskAlpha(eased),
  )},
  ${maskAlpha(1)} ${foot(CHROME_FADE_END_PX)},
  ${easedRamp(
    (t) => foot(CHROME_HEIGHT_PX + (1 - t) * CHROME_RAMP_PX),
    (eased) => maskAlpha(1 - eased),
  )})`

/**
 * The chrome fade as plain stops, for a caller that owns its own gradient — the reading column
 * fades out at BOTH ends on its own terms, and the citation graph fades at the top only.
 *
 * Returned without the surrounding `linear-gradient(to bottom, ...)` so each can finish it its
 * own way. Fixed pixels, not the custom properties above: these callers know what they have.
 */
export function chromeFadeStops(): string {
  return `${maskAlpha(0)} 0px,
  ${easedRamp(
    (t) => `${(CHROME_HEIGHT_PX + t * CHROME_RAMP_PX).toFixed(2)}px`,
    (eased) => maskAlpha(eased),
  )}`
}

/**
 * The same thing at the other end: opaque until the fade begins, then easing out to leave the
 * footer's own depth completely clear.
 *
 * The footer is transparent — it is a row of controls, not a panel — so whatever is behind it
 * shows through unless the thing behind it stops. Anything that fills the viewport under a
 * footer needs this or it draws right down to the bottom edge.
 */
export function chromeFadeStopsBottom(): string {
  return `${maskAlpha(1)} calc(100% - ${CHROME_FADE_END_PX}px),
  ${easedRamp(
    (t) => `calc(100% - ${(CHROME_FADE_END_PX - t * CHROME_RAMP_PX).toFixed(2)}px)`,
    (eased) => maskAlpha(1 - eased),
  )}`
}
